import { useState, useEffect } from "react";

const Thumbnail = ({ screenshot, rect, strategy, onClick }) => {
  // Responsive dimensions for the thumbnail container
  const [thumbDimensions, setThumbDimensions] = useState({ w: 120, h: 120 });
  useEffect(() => {
    const checkScreenSize = () => {
      const isMobile = document.documentElement.clientWidth < 768;
      setThumbDimensions(isMobile ? { w: 120, h: 96 } : { w: 150, h: 120 });
    };
    checkScreenSize();
    window.addEventListener("resize", checkScreenSize);
    return () => window.removeEventListener("resize", checkScreenSize);
  }, []);

  // Placeholder for loading or invalid data
  if (!screenshot || !screenshot.screenshot.data || !rect) {
    return (
      <div
        className="bg-muted border animate-pulse rounded-md"
        style={{ width: thumbDimensions.w, height: thumbDimensions.h }}
      />
    );
  }

  const { data, width: imgW, height: imgH } = screenshot.screenshot;
  const { w: thumbW, h: thumbH } = thumbDimensions;

  // 1. Determine a scale that makes the element prominent but with context
  const hScale = thumbH / (rect.height * 2.5); // Target 40% of thumb height
  const wScale = thumbW / (rect.width * 2.5); // Target 40% of thumb width
  const scale = Math.min(hScale, wScale);

  if (!isFinite(scale) || scale <= 0) {
    // Fallback for invalid rect dimensions
    return <div className="bg-red-100 border border-red-500" />;
  }

  // 2. Center the view on the element
  const viewCenterX = rect.left + rect.width / 2;
  const viewCenterY = rect.top + rect.height / 2;

  // 3. Calculate the top-left of the image inside the thumbnail
  // This positions the view's center at the thumbnail's center
  const imgLeft = thumbW / 2 - viewCenterX * scale;
  const imgTop = thumbH / 2 - viewCenterY * scale;

  // 4. Calculate highlight box position relative to the thumbnail container
  const isDesktopData = strategy === "desktop";
  const BASE_OFFSET = isDesktopData ? 5 : 0;
  const HORIZONTAL_OFFSET = BASE_OFFSET * scale;
  const highlightLeft = rect.left * scale + imgLeft - HORIZONTAL_OFFSET;
  const highlightTop = rect.top * scale + imgTop;

  return (
    <button
      type="button"
      onClick={onClick}
      className="border overflow-hidden relative focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-ring rounded-md shadow-sm group bg-muted"
      style={{ width: thumbW, height: thumbH }}
    >
      <img
        src={data}
        alt="Contextual thumbnail of failing element"
        className="absolute max-w-none"
        style={{
          width: imgW * scale,
          height: imgH * scale,
          left: imgLeft,
          top: imgTop,
        }}
      />
      {/* Red filled background for highlighted element */}
      <div
        className="absolute"
        style={{
          left: highlightLeft,
          top: highlightTop,
          width: rect.width * scale,
          height: rect.height * scale,
          backgroundColor: "rgba(255, 0, 0, 0.3)",
          border: "2px solid red",
        }}
      />
    </button>
  );
};

export default Thumbnail;
